import React, { useState } from 'react'
import { TextInput } from 'uiComponents/input'
import { showWarningMessage } from 'uiComponents/message'
import { Permission, UserPin } from 'settings/schema'
import { CopyIcon } from 'uiComponents/buttons/copyIcon'
import classNames from 'classnames'
import { delay } from 'utils'
import { PermissionGroup, PermissionGroupContent } from '../components/permissionGroup'
import { PermissionToggle } from '../components/permissionToggle'
import './pointOfSale.scss'
import { useTranslation } from 'translations'

const PIN_LENGTH = 6

interface PointOfSalePermissionGroupProps {
    userId: number | null
    userPin: UserPin | null
    isPosLoginGranted: boolean
    posUserManagementEnabled: boolean
    isAdminGranted: boolean
    generateUserPin: (userId: number) => Promise<UserPin | null>
    regenerateUserPin: (pinId: number) => Promise<UserPin | null>
    handlePermissionClick: (permission: Permission, granted: boolean) => void
}

export const PointOfSalePermissionGroup = ({
    userId,
    userPin,
    isPosLoginGranted,
    posUserManagementEnabled,
    isAdminGranted,
    generateUserPin,
    regenerateUserPin,
    handlePermissionClick,
}: PointOfSalePermissionGroupProps) => {
    const { t } = useTranslation()
    const [isGeneratingPin, setIsGeneratingPin] = useState(false)
    const [randomPin, setRandomPin] = useState('')

    const generateRandomPin = (length: number) => {
        let result = ''
        for (let i = 0; i < length; i++) {
            result += Math.floor(Math.random() * 10).toString()
        }
        return result
    }

    const revealPinGradually = async (pin: string) => {
        for (let i = 0; i <= PIN_LENGTH; i++) {
            const revealed = pin.substring(0, i)

            const stepStartTime = Date.now()
            while (Date.now() - stepStartTime < 100) {
                setRandomPin(revealed + generateRandomPin(PIN_LENGTH - i))
                await delay(25)
            }
        }
    }

    const handleGeneratePin = async () => {
        const minGeneratingTime = 300
        if (!userId) {
            showWarningMessage(t('settings.users.form.pos.saveBeforePin'))
            return
        }

        const startTime = Date.now()
        setIsGeneratingPin(true)

        const shuffleInterval = setInterval(() => {
            setRandomPin(generateRandomPin(PIN_LENGTH))
        }, 25)

        const newUserPin = await (userPin ? regenerateUserPin(userPin.id) : generateUserPin(userId))

        const endTime = Date.now()
        const generatingTime = endTime - startTime
        if (generatingTime < minGeneratingTime) {
            await delay(minGeneratingTime - generatingTime)
        }

        clearInterval(shuffleInterval)
        if (newUserPin) {
            await revealPinGradually(newUserPin.pin)
        }
        setIsGeneratingPin(false)
    }

    let pinValue = ''
    if (isGeneratingPin) {
        pinValue = randomPin
    } else if (userPin) {
        pinValue = userPin.pin || '******'
    }

    return (
        <PermissionGroup
            brandedTitle={t('settings.users.form.groups.pointOfSale')}
            expanded={isPosLoginGranted && posUserManagementEnabled}
            className="pos-permissions"
        >
            <div className="row">
                <PermissionToggle
                    permission="pos_login"
                    disabled={isAdminGranted || !posUserManagementEnabled}
                    handleClick={handlePermissionClick}
                />
            </div>
            {!posUserManagementEnabled && (
                <div className="pos-management-required">{t('settings.users.form.pos.managementRequired')}</div>
            )}
            <PermissionGroupContent height={78}>
                <div
                    className={classNames('pin-wrapper', {
                        'has-pin': userPin?.pin && !isGeneratingPin,
                        generating: isGeneratingPin,
                    })}
                >
                    <div className="pin-title">{t('settings.users.form.pos.pinTitle')}</div>
                    <div className="generate-pin" onClick={handleGeneratePin}>
                        {userPin ? t('settings.users.form.pos.generateNew') : t('settings.users.form.pos.generate')}
                    </div>
                    <TextInput
                        type="text"
                        name="pin"
                        disabled
                        value={pinValue}
                        placeholder={t('settings.users.form.pos.noPin')}
                    />
                    {userPin?.pin && !isGeneratingPin && <CopyIcon text={userPin.pin} />}
                    <div className="pin-explanation">{t('settings.users.form.pos.pinExplanation')}</div>
                </div>
            </PermissionGroupContent>
        </PermissionGroup>
    )
}
